import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { useSession } from './lib/authClient';
import { Spinner } from './components/motion';
import { useDisableZoom } from './hooks/useDisableZoom';

import SignupPage from './pages/SignupPage';
import SigninPage from './pages/SigninPage';
import Dashboard from './pages/Dashboard';
import StrategyBuilder from './pages/StrategyBuilder';
import ActiveStrategies from './pages/ActiveStrategies';
import TradingFloor from './pages/TradingFloor';
import Portfolio from './pages/Portfolio';
import TradeHistory from './pages/TradeHistory';
import Backtesting from './pages/Backtesting';
import Research from './pages/Research';
import ChartAnalysis from './pages/ChartAnalysis';
import AccountPage from './pages/AccountPage';
import SettingsPage from './pages/SettingsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import LandingPage from './pages/LandingPage';
import Community from './pages/Community';
import Sherlock from './pages/Sherlock';

// Apollo
import ApolloLayout from './components/Apollo/ApolloLayout';
import BrokerList from './pages/apollo/BrokerList';
import BrokerDashboard from './pages/apollo/BrokerDashboard';
import NewBroker from './pages/apollo/NewBroker';
import ChatPanel from './pages/apollo/ChatPanel';
import WorkflowEditor from './pages/apollo/WorkflowEditor';
import FundManager from './pages/apollo/FundManager';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000,
    },
  },
});

const isPWA = window.matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone === true;

function HomeRedirect() {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  if (session?.user) return <Navigate to="/home/dashboard" replace />;

  // PWA (installed app): skip landing page, go straight to sign-in
  if (isPWA) return <Navigate to="/signin" replace />;

  // Browser: show landing page
  return <LandingPage />;
}

function App() {
  useDisableZoom();

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster
        position="top-right"
        offset={60}
        dir="ltr"
        toastOptions={{
          style: {
            background: 'hsl(240 4% 10%)',
            border: '1px solid hsl(240 4% 22%)',
            color: 'hsl(0 0% 95%)',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signin" element={<SigninPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />

          {/* App shell — protected */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            {/* Home section */}
            <Route path="/home/dashboard" element={<Dashboard />} />
            <Route path="/home/portfolio" element={<Portfolio />} />
            <Route path="/home/history" element={<TradeHistory />} />
            <Route path="/home/community" element={<Community />} />

            {/* Strategies section */}
            <Route path="/strategies/builder" element={<StrategyBuilder />} />
            <Route path="/strategies/builder/:id" element={<StrategyBuilder />} />
            <Route path="/strategies/manager" element={<ActiveStrategies />} />
            <Route path="/strategies/trading-floor" element={<TradingFloor />} />
            <Route path="/strategies/trading-floor/:strategyId" element={<TradingFloor />} />
            {/* Apollo section — nested under ApolloLayout for persistent drawer */}
            <Route path="/apollo" element={<ApolloLayout />}>
              <Route path="brokers" element={<BrokerList />} />
              <Route path="brokers/new" element={<NewBroker />} />
              <Route path="brokers/:brokerId" element={<BrokerDashboard />} />
              <Route path="chat" element={<ChatPanel />} />
              <Route path="chat/:brokerId" element={<ChatPanel />} />
              <Route path="chat/:brokerId/:sessionId" element={<ChatPanel />} />
              <Route path="workflow/:brokerId" element={<WorkflowEditor />} />
              <Route path="fund-manager" element={<FundManager />} />
            </Route>

            {/* Legacy redirect */}
            <Route path="/strategies/apollo" element={<Navigate to="/apollo/brokers" replace />} />

            {/* Analysis section */}
            <Route path="/analysis/backtesting" element={<Backtesting />} />
            <Route path="/analysis/research" element={<Research />} />
            <Route path="/analysis/charts" element={<ChartAnalysis />} />
            <Route path="/analysis/sherlock" element={<Sherlock />} />

            {/* Account section */}
            <Route path="/account/portfolio" element={<AccountPage />} />
            <Route path="/account/billing" element={<AccountPage />} />
            <Route path="/account/notifications" element={<AccountPage />} />

            {/* Settings section */}
            <Route path="/settings/general" element={<SettingsPage />} />
            <Route path="/settings/brokers" element={<SettingsPage />} />
            <Route path="/settings/market-data" element={<SettingsPage />} />

            {/* Legacy redirects */}
            <Route path="/dashboard" element={<Navigate to="/home/dashboard" replace />} />
            <Route path="/portfolio" element={<Navigate to="/home/portfolio" replace />} />
            <Route path="/trades" element={<Navigate to="/home/history" replace />} />
            <Route path="/strategies/new" element={<Navigate to="/strategies/builder" replace />} />
            <Route path="/strategies" element={<Navigate to="/strategies/manager" replace />} />
            <Route path="/trading-floor" element={<Navigate to="/strategies/trading-floor" replace />} />
            <Route path="/trading-floor/:strategyId" element={<Navigate to="/strategies/trading-floor" replace />} />
            <Route path="/backtesting" element={<Navigate to="/analysis/backtesting" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
